/**
 * Engine normalisation + shared result contract for the "Find usages"
 * definition-text search.
 *
 * Mirrors `objectDefinitionService`'s `normalizeEngine` 5-way branch on purpose:
 * find-usages is the search-shaped sibling of the definition-fetch it reuses
 * catalog knowledge from, and the two must agree on what "this engine" means.
 */
import type { DatabaseEngineId } from '@/core/engines';
import type { DependencyItem } from '../../core/types/workspaceTypes';

export type FindUsagesEngine = 'mssql' | 'pgsql' | 'sqlite' | 'mysql' | 'oracle';

export function normalizeFindUsagesEngine(engine?: DatabaseEngineId | string): FindUsagesEngine {
  if (engine === 'pgsql') return 'pgsql';
  if (engine === 'sqlite') return 'sqlite';
  if (engine === 'mysql') return 'mysql';
  if (engine === 'oracle') return 'oracle';
  return 'mssql';
}

/** Engines this search has no SQL for. */
const UNSUPPORTED_ENGINES: ReadonlySet<string> = new Set(['kusto']);

/**
 * Whether "Find usages" can run against this engine at all.
 *
 * Must be checked before offering the entry point. `normalizeFindUsagesEngine`
 * falls back to `mssql` for anything it does not recognise, which is right for
 * the four SQL engines but wrong for Kusto/ADX: it speaks KQL and has no
 * `sys.sql_modules`, so an unguarded menu item hands a Kusto user a T-SQL
 * catalog query and a parser error. Kusto's own index-backed references are a
 * separate concern — this is only about not offering a search that cannot run.
 */
export function isFindUsagesSupportedEngine(engine?: DatabaseEngineId | string): boolean {
  return !!engine && !UNSUPPORTED_ENGINES.has(engine);
}

/**
 * Per-object outcome of the text scan. Deliberately three-valued: a text search
 * that quietly drops the objects it could not read would answer "no usages"
 * with the same confidence as a genuine zero, which is the single biggest
 * honesty risk in this feature (F09 watch-outs).
 *
 * - `matched`    — the definition was read and contains the search term.
 * - `unreadable` — the definition came back NULL/empty (encrypted module, or no
 *                  VIEW DEFINITION / SHOW VIEW grant). We cannot say either way.
 * - `truncated`  — only a prefix of the definition was searchable (Oracle views:
 *                  `ALL_VIEWS.TEXT` is a LONG and unusable in a WHERE, so we
 *                  search `TEXT_VC`, which stops at 4000 characters).
 *
 * Declared in the shared workspace type layer because the Dependency Viewer tab
 * persists the outcome in its content; re-exported here so the search code can
 * import its whole vocabulary from one place.
 */
export type { FindUsagesSearchState, FindUsagesOutcome } from '../../core/types/workspaceTypes';

export type FindUsagesRow = {
  item: DependencyItem;
  state: import('../../core/types/workspaceTypes').FindUsagesSearchState;
};
